/**
 * @file statementDelivery.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-10-02
 *
 * What became of a statement's deliveries, read from its delivery log — the
 * `deliveries` array that /api/statements/by-account and
 * /api/statements/admin/runs/:id both attach (one entry per
 * invoicing.email_queue row, oldest first, with the provider outcome as
 * `delivery_status`). Shared by the contact modal's Statements panel and the
 * run history so the two flag a statement the same way.
 *
 * The rule the flags used to break: "did any attempt ever fail?" stays true
 * for ever, so a statement that bounced and was then resent to a corrected
 * address kept its failure flag. ORDER decides it now — a failure that a later
 * attempt OF THE SAME KIND followed has been answered, and for an email the
 * flag gives way to a resent mark. If the resend fails in turn it becomes the
 * failure and the flag comes back.
 *
 * "Same kind" is the attempt's recipient_type, one lane each:
 *   customer — Email to Customer (batch or redeliver). Only this answers a
 *              customer bounce: a copy emailed anywhere else does not reach
 *              the customer.
 *   office   — the batch's office review copy AND redeliver's "Send to Email"
 *              (any typed address — the server logs both as 'office').
 *   print    — a failed print is answered by a later print, or, for the
 *              legacy rows from before successful prints were logged, by the
 *              statement itself recording that it was printed. A batch print
 *              the printer did not confirm is logged 'print_unconfirmed'
 *              (since 2026-10-02) and counts as a failed print until a later
 *              print of that statement is confirmed. So does 'print_pending':
 *              sent and not yet confirmed — the print is still running, or it
 *              stopped (a restart) before the printer answered.
 *
 * Derived, not stored: there is no "resent" column. A resend is simply an
 * attempt logged after a failed one of the same kind.
 */

/** Attempts that went by email (batch / review / redeliver), not print or save. */
const EMAIL_RECIPIENT_TYPES = new Set(['customer', 'office']);

/** Provider outcomes that mean the email did not arrive. */
const UNDELIVERED = new Set(['bounced', 'complained', 'rejected', 'failed']);

export const isEmailAttempt = (d) => EMAIL_RECIPIENT_TYPES.has(d?.recipient_type);

/** Batch-print rows the printer has not confirmed (see services/statementPrintLog.js). */
const PRINT_NOT_CONFIRMED = new Set(['print_unconfirmed', 'print_pending']);

/** Refused when sent, or bounced / complained / rejected afterwards, or a print the printer did not confirm. */
export const attemptFailed = (d) => d?.status === 'failed' || PRINT_NOT_CONFIRMED.has(d?.status)
  || UNDELIVERED.has(d?.delivery_status);

const bouncedOrSpam = (d) => d?.delivery_status === 'bounced' || d?.delivery_status === 'complained';

/**
 * @param {Array} deliveries - the statement's delivery log, oldest first
 * @param {object} [statement] - the statement row; only `delivery_method` is
 *   read, to answer a legacy failed print (see the header)
 * @returns {{
 *   failure: object|null,  the failure the resend answered; with no resend,
 *                          the most recent failed email attempt still standing
 *   resent: object|null,   the latest email that answered a failed email
 *   standing: object[],    failures nothing later of their kind answered
 *   failed: boolean,       a standing refusal / rejection / failed print ("!" flag)
 *   bounced: boolean,      a standing bounce or spam report (warning flag)
 *   delivered: boolean     the provider confirmed an email delivered
 * }}
 */
export function deliveryOutcome(deliveries, statement) {
  const list = Array.isArray(deliveries) ? deliveries : [];

  // One lane per kind of attempt, each in log order, remembering log position.
  const lanes = new Map();
  list.forEach((d, i) => {
    const kind = d?.recipient_type || 'unknown';
    if (!lanes.has(kind)) lanes.set(kind, []);
    lanes.get(kind).push({ d, i });
  });

  // Printed per the statement, with no successful print in the log: the
  // success went unlogged (the legacy batch print), so it answers a failure.
  const printedUnlogged = statement?.delivery_method === 'printed'
    && !list.some(d => d?.recipient_type === 'print' && d.status === 'printed');

  const standing = [];
  let resend = null; // { d, i, failure } — the latest email that answered a failed one
  for (const [kind, entries] of lanes) {
    let last = -1;
    entries.forEach((e, k) => { if (attemptFailed(e.d)) last = k; });
    if (last < 0) continue;
    const failure = entries[last];
    // Everything after the lane's last failure succeeded; the newest answers it.
    const answer = last < entries.length - 1 ? entries[entries.length - 1] : null;
    if (answer) {
      if (EMAIL_RECIPIENT_TYPES.has(kind) && (!resend || answer.i > resend.i)) {
        resend = { ...answer, failure: failure.d };
      }
    } else if (!(kind === 'print' && printedUnlogged)) {
      standing.push(failure);
    }
  }
  standing.sort((x, y) => x.i - y.i);
  const standingEmail = standing.filter(e => isEmailAttempt(e.d));

  return {
    failure: resend ? resend.failure : (standingEmail.length ? standingEmail[standingEmail.length - 1].d : null),
    resent: resend ? resend.d : null,
    standing: standing.map(e => e.d),
    failed: standing.some(e => !bouncedOrSpam(e.d)),
    bounced: standing.some(e => bouncedOrSpam(e.d)),
    delivered: list.some(d => isEmailAttempt(d) && d.delivery_status === 'delivered')
  };
}

/** How an attempt failed, in words. */
export function failureLabel(d) {
  if (!d) return 'failed';
  if (d.status === 'print_unconfirmed') return 'not confirmed printed';
  if (d.status === 'print_pending') return 'sent to the printer, not confirmed yet';
  if (d.delivery_status === 'bounced') return 'bounced';
  if (d.delivery_status === 'complained') return 'marked as spam';
  if (d.delivery_status === 'rejected') return 'rejected';
  return 'failed to send';
}

/**
 * Is a recipient-type badge an unanswered failure? (red badge) — the same
 * standing failures as the "!" flag, on the badge for their kind.
 */
export function badgeFailed(outcome, recipientType) {
  return (outcome?.standing || []).some(d => d.recipient_type === recipientType && !bouncedOrSpam(d));
}

/**
 * Tooltip for the resent mark: when, to whom, whether it has arrived, and what
 * it replaced, e.g.
 *   "Resent 2026-10-01 17:35 to a@gmail.com · delivered 17:35 · by Cam Seymour
 *    (earlier bounced: a@tiscali.co.uk, 2026-09-30 16:13)"
 * Times are London, as the server formats them.
 */
export function resentTitle(outcome) {
  const r = outcome?.resent;
  if (!r) return '';
  const f = outcome.failure;
  const parts = [`Resent${r.ts ? ` ${r.ts}` : ''} to ${r.email || 'an unrecorded address'}`];
  if (r.delivery_status === 'delivered') {
    const sameDay = r.delivery_at && r.ts && r.delivery_at.slice(0, 10) === r.ts.slice(0, 10);
    parts.push(`delivered${r.delivery_at ? ` ${sameDay ? r.delivery_at.slice(11) : r.delivery_at}` : ''}`);
  } else {
    parts.push('awaiting delivery confirmation');
  }
  if (r.by_name) parts.push(`by ${r.by_name}`);
  const earlier = f
    ? ` (earlier ${failureLabel(f)}: ${f.email || 'unrecorded address'}${f.ts ? `, ${f.ts}` : ''})`
    : '';
  return parts.join(' · ') + earlier;
}
